import React, { useState, useEffect, useCallback } from "react";
import { WidthProvider, Responsive } from "react-grid-layout";

import { useSelector } from 'react-redux';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Menu, MenuItem } from '@mui/material';

import RecentServices from './RecentServices';
import CSMSHealth from './CSMSHealth';
import Favorites from './Favorites';
import BlogRecentPosts from './BlogRecentPosts';
import VODUsage from './VODUsage';
import VODConversionStatus from './VODConversionStatus';
import { Grid } from './Grid'

import '@resources/assets/styles/dashboard.scss'
import '../../../../../node_modules/react-grid-layout/css/styles.css';
import '../../../../../node_modules/react-resizable/css/styles.css';
import { useTranslation } from 'react-i18next';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const CommonWidget = ({ showWidgetList, usedData, setUsedData }) => {
    const { t } = useTranslation();

    const sidebarCollapsed = useSelector(state => state.ui.sidebarCollapsed);
    const allBreakpoints = ["lg", "md", "sm", "xs", "xxs"];
    const breakpointCols = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
    const gap = 10;
    const rowHeight = 80;
    const [gridWidth, setGridWidth] = useState(0);
    const [maxHeight, setMaxHeight] = useState(0);

    const [currentBreakpoint, setCurrentBreakpoint] = useState("lg");
    const [layouts, setLayouts] = useState(usedData || {});
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuItemId, setMenuItemId] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [firstLoad, setFirstLoad] = useState(true);
    const [originalWidths, setOriginalWidths] = useState({});
    const [prevBreakpoint, setPrevBreakpoint] = useState(null);
    const [movingItem, setMovingItem] = useState({});
    const [breakpointIndices] = useState({
        lg: 0, md: 1, sm: 2, xs: 3, xxs: 4
    });
    const [droppedItem] = useState({
        i: '__dropping-elem__',
        w: 2,
        h: 2
    });

    // 🔹 Initialize layouts from props
    useEffect(() => {
        setLayouts(usedData || {});

        const widths = {};
        Object.keys(usedData || {}).forEach(bp => {
            if (usedData[bp] && usedData[bp].length > 0) {
                widths[bp] = {};
                usedData[bp].forEach(item => {
                    if (!widths[bp][item.i]) {
                        widths[bp][item.i] = item.w;
                    }
                });
            }
        });
        setOriginalWidths(widths);
    }, [usedData]);

    // 🔹 Sync layouts to parent component
    // This is the key fix - we're using useEffect to batch updates to the parent
    useEffect(() => {
        // Only update parent when layouts has actually changed and is not empty
        if (Object.keys(layouts).length > 0 && !firstLoad) {
            const timer = setTimeout(() => {
                setUsedData(layouts);
                localStorage.setItem('gridLayoutConfig', JSON.stringify(layouts));
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [layouts, setUsedData, firstLoad]);

    useEffect(() => {
        const updateContainerWidth = () => {
            const container = document.querySelector('.widgetGrid-container');
            const newContainerWidth = container ? container.offsetWidth - 70 : 0;
            setGridWidth(newContainerWidth / breakpointCols[currentBreakpoint]);
        };

        console.log('currentBreakpoint', currentBreakpoint)
        window.addEventListener('resize', updateContainerWidth);
        updateContainerWidth();

        return () => window.removeEventListener('resize', updateContainerWidth);
    }, [currentBreakpoint, breakpointCols]);

    // maxHeight 계산 - 가장 아래 요소보다 3 더 큰 값으로 설정
    useEffect(() => {
        if (layouts[currentBreakpoint] && layouts[currentBreakpoint].length > 0) {
            // 가장 아래 있는 요소 찾기 (y + h가 가장 큰 요소)
            console.log('layouts', layouts, layouts[currentBreakpoint]);
            const lowestItem = layouts[currentBreakpoint].reduce((lowest, item) => {
                const bottomEdge = item.y + item.h;
                return bottomEdge > (lowest.y + lowest.h) ? item : lowest;
            }, layouts[currentBreakpoint][0]);
            console.log('lowestItem', lowestItem);
            // 가장 아래 요소보다 3칸 더 큰 값으로 maxHeight 설정
            const calculatedMaxHeight = lowestItem ? lowestItem.y + lowestItem.h + 3 : 3;
            setMaxHeight(calculatedMaxHeight);
        } else {
            // 요소가 없는 경우 기본값 설정
            setMaxHeight(5);
        }
    }, [layouts, currentBreakpoint]);

    // sidebarCollapsed 변경 시 컨테이너 너비 업데이트
    useEffect(() => {
        const container = document.querySelector('.widgetGrid-container');
        const newContainerWidth = container ? container.offsetWidth - 70 : 0;
        console.log('sidebarCollapsed changed, newContainerWidth:', newContainerWidth);
        setGridWidth(newContainerWidth / breakpointCols[currentBreakpoint]);
    }, [showWidgetList, sidebarCollapsed, currentBreakpoint, breakpointCols]);

    // 레이아웃 순서 가져오는 함수
    const getOrder = (layout) => {
        // y축 기준 정렬 후 x축 기준으로 정렬하여 아이템 ID 배열 반환
        return [...layout]
            .sort((a, b) => {
                if (a.y === b.y) return a.x - b.x;
                return a.y - b.y;
            })
            .map(item => item.i);
    };

    // 특정 breakpoint에 대한 레이아웃 생성 함수
    const generateBasicLayout = (sourceLayout, cols) => {
        const layout = [];

        // 고정된 격자 방식으로 레이아웃 생성 (왼쪽→오른쪽, 위→아래)
        let x = 0;
        let y = 0;

        for (const item of sourceLayout) {
            const w = Math.min(item.w, cols);
            const h = item.h || 1;

            // 현재 행에 아이템을 배치할 공간이 없으면 다음 행으로 이동
            if (x + w > cols) {
                x = 0;
                y++;
            }

            // 아이템 배치
            layout.push({ ...item, x, y, w, h, i: item.i });

            // x 좌표 업데이트
            x += w;
        }

        return layout;
    };

    const generateLayoutForBreakpoint = (sourceLayout, cols) => {
        // 기본 레이아웃 생성 (고정된 위치에 위젯 배치)
        return generateBasicLayout(sourceLayout, cols);
    };

    // 첫 로드 시 모든 브레이크포인트에 대한 레이아웃 생성
    useEffect(() => {
        if (firstLoad) {
            let baseLayout;
            let updatedLayouts = { ...layouts };

            // 가장 높은 우선순위의 breakpoint 레이아웃 찾기
            for (const bp of allBreakpoints) {
                if (layouts[bp] && layouts[bp].length > 0) {
                    baseLayout = layouts[bp];
                    break;
                }
            }

            // 기준 레이아웃이 있으면 나머지 breakpoint의 레이아웃 생성
            if (baseLayout) {
                const order = getOrder(baseLayout);
                const orderedItems = order
                    .map(id => baseLayout.find(item => item.i === id))
                    .filter(Boolean);

                for (const bp of allBreakpoints) {
                    if (!updatedLayouts[bp] || updatedLayouts[bp].length === 0) {
                        updatedLayouts[bp] = generateLayoutForBreakpoint(orderedItems, breakpointCols[bp]);
                    }
                }

                setLayouts(updatedLayouts);
            }

            setFirstLoad(false);
        }
    }, [firstLoad, layouts, allBreakpoints, breakpointCols]);

    // 🧩 브레이크포인트 변경 시 관련 상태 업데이트
    const handleBreakpointChange = (breakpoint, cols) => {
        const container = document.querySelector('.widgetGrid-container');
        const newContainerWidth = container ? container.offsetWidth - 70 : 0;
        setGridWidth(newContainerWidth / breakpointCols[breakpoint]);

        console.log('🔁 Breaking point changed:', breakpoint, 'cols:', cols);

        // 화면 크기 변화 방향 감지 (작아지는 중인지, 커지는 중인지)
        const isScreenShrinking = prevBreakpoint &&
            breakpointIndices[breakpoint] > breakpointIndices[prevBreakpoint];

        console.log(`화면 ${isScreenShrinking ? '축소' : '확대'} 중, ${prevBreakpoint || 'initial'} -> ${breakpoint}`);

        // 이전 브레이크포인트 저장
        setPrevBreakpoint(breakpoint);
        setCurrentBreakpoint(breakpoint);

        // 해당 breakpoint의 레이아웃이 있으면 사용
        if (layouts[breakpoint] && layouts[breakpoint].length > 0) {
            return;
        }

        // 레이아웃이 없는 경우, 가장 가까운 존재하는 breakpoint 레이아웃을 찾아서 변환
        let sourceBreakpoint = null;
        for (const bp of allBreakpoints) {
            if (layouts[bp] && layouts[bp].length > 0) {
                sourceBreakpoint = bp;
                break;
            }
        }

        if (sourceBreakpoint) {
            const sourceLayout = layouts[sourceBreakpoint];
            const order = getOrder(sourceLayout);
            const orderedItems = order
                .map(id => {
                    const item = sourceLayout.find(item => item.i === id);
                    if (item) {
                        // 원본 너비가 있다면 사용
                        const originalWidth = originalWidths[sourceBreakpoint]?.[item.i];
                        if (originalWidth) {
                            return { ...item, w: originalWidth };
                        }
                        return { ...item };
                    }
                    return null;
                })
                .filter(Boolean);

            // 기본 레이아웃 생성 (고정된 위치에 위젯 배치)
            const newLayout = generateBasicLayout(orderedItems, cols);

            // 현재 브레이크포인트의 레이아웃만 업데이트
            setLayouts(prev => ({
                ...prev,
                [breakpoint]: newLayout
            }));
        }
    };

    // Remove widget handler
    const handleRemoveItem = (componentName) => {
        console.log('usedData', usedData, componentName);
        // 제거 시 레이아웃은 업데이트하되, 다른 위젯의 위치는 변경하지 않음
        setLayouts(prevLayouts => {
            const updatedLayouts = { ...prevLayouts };
            Object.keys(updatedLayouts).forEach(breakpoint => {
                updatedLayouts[breakpoint] = updatedLayouts[breakpoint].filter(
                    item => item.i !== componentName
                );
            });
            return updatedLayouts;
        });
    };

    // Menu handlers
    const handleDelete = () => {
        if (menuItemId !== null) {
            handleRemoveItem(menuItemId);
            setAnchorEl(null);
            setMenuItemId(null);
        }
    };

    const handleMenuClick = (event, componentName) => {
        setAnchorEl(event.currentTarget);
        setMenuItemId(componentName);
    };

    // Drag handlers
    const handleDrag = (_layout, layoutItem, event) => {
        if (layoutItem.i === "__dropping-elem__") {
            setMovingItem(event);
        }
    };

    const handleDrop = (_layout, _layoutItem, event) => {
        event.preventDefault();
        const data = event.dataTransfer.getData("text/plain");
        const parsedDropData = JSON.parse(data);
        console.log('movingItem', parsedDropData, movingItem);

        const newLayoutItem = {
            i: parsedDropData.i,
            x: movingItem.x || 0,
            y: movingItem.y || 0,
            w: parsedDropData.w || 2,
            h: parsedDropData.h || 2
        };

        setLayouts(prevLayouts => {
            const updatedLayouts = { ...prevLayouts };
            allBreakpoints.forEach(bp => {
                if (!updatedLayouts[bp]) {
                    updatedLayouts[bp] = [];
                }
                updatedLayouts[bp] = [...updatedLayouts[bp], newLayoutItem];
            });
            return updatedLayouts;
        });
    };

    // 🧩 Drag/Resize 후 현재 layout 업데이트
    const updateLayout = (newLayout) => {
        // 레이아웃의 순서를 추출 (y, x 기준으로 정렬)
        const order = getOrder(newLayout);

        // 현재 breakpoint에서 변경된 레이아웃을 기준으로 다른 breakpoint 레이아웃 동기화
        syncLayoutsFrom(currentBreakpoint, newLayout, order);
    };

    const syncLayoutsFrom = (sourceBreakpoint, updatedLayout, order) => {
        const newLayouts = { ...layouts };

        // 업데이트된 레이아웃에서 각 아이템의 원본 너비 저장
        const newOriginalWidths = { ...originalWidths };
        if (!newOriginalWidths[sourceBreakpoint]) {
            newOriginalWidths[sourceBreakpoint] = {};
        }

        updatedLayout.forEach(item => {
            newOriginalWidths[sourceBreakpoint][item.i] = item.w;
        });

        // 현재 breakpoint의 레이아웃 저장
        newLayouts[sourceBreakpoint] = updatedLayout;

        // 다른 모든 breakpoint에 대해서는 순서를 기준으로 레이아웃 재생성
        allBreakpoints.forEach(bp => {
            if (bp !== sourceBreakpoint) {
                // 순서대로 아이템을 가져와서 새 레이아웃 생성
                const orderedItems = order
                    .map(id => updatedLayout.find(item => item.i === id))
                    .filter(Boolean);

                // 너비 조정 없이 기본 레이아웃 생성
                const layout = generateBasicLayout(orderedItems, breakpointCols[bp]);

                // 각 브레이크포인트의 원본 너비 값이 없는 경우 저장
                if (!newOriginalWidths[bp]) {
                    newOriginalWidths[bp] = {};
                }

                layout.forEach(item => {
                    if (!newOriginalWidths[bp][item.i]) {
                        newOriginalWidths[bp][item.i] = item.w;
                    }
                });

                newLayouts[bp] = layout;
            }
        });

        // 상태 업데이트
        setOriginalWidths(newOriginalWidths);
        setLayouts(newLayouts);
    };

    const RenderWidget = ({ componentName }) => {
        const lowerCaseComponentName = componentName.toLowerCase();
        switch (lowerCaseComponentName) {
            case 'recentservices':
                return <RecentServices />;
            case 'csmshealth':
                return <CSMSHealth />;
            case 'favorites':
                return <Favorites />;
            case 'blogrecentposts':
                return <BlogRecentPosts />;
            case 'vodusage':
                return <VODUsage />;
            case 'vodconversionstatus':
                return <VODConversionStatus />;
            default:
                return <div>Unknown Widget: {componentName}</div>;
        }
    };

    const createGrid = (rows: number, cols: number) =>
        Array.from({ length: rows }, () =>
            Array(cols).fill(null)
        );

    return (
        <div className="container_widget_grid">
            <div className="container_widget_grid_background" style={{ display: isDragging ? 'block' : 'none' }}>
                {
                    maxHeight > 0 && (
                        <Grid
                            gridColumnCount={breakpointCols[currentBreakpoint]}
                            grid={createGrid(maxHeight, breakpointCols[currentBreakpoint])}
                            gridWidth={gridWidth}
                            gridHeight={rowHeight}
                            gap={gap}
                        />
                    )}
            </div>

            <div className="container_widget_grid_content">
                <ResponsiveReactGridLayout
                    className="layout_widget_grid"
                    layouts={layouts}
                    rowHeight={rowHeight}
                    onBreakpointChange={handleBreakpointChange}
                    onDragStart={() => setIsDragging(true)}
                    onDragStop={(layout) => { setIsDragging(false); updateLayout(layout); }}
                    onResizeStart={() => setIsDragging(true)}
                    onResizeStop={(layout) => { setIsDragging(false); updateLayout(layout); }}
                    draggableHandle=".box_drag_handle"
                    onDrag={handleDrag}
                    onDrop={handleDrop}
                    isDroppable={true}
                    droppingItem={{ ...droppedItem, i: '__dropping-elem__' }}
                    breakpoints={{ lg: 1280, md: 992, sm: 767, xs: 480, xxs: 0 }}
                    cols={breakpointCols}
                >
                    {layouts[currentBreakpoint] && layouts[currentBreakpoint].map(item => (
                        <div key={item.i} className="container_draggable_content">
                            <div className="box_widget_menu">
                                <div className="box_widget_menu_title">
                                    <DragIndicatorIcon className="box_drag_handle" />
                                    <div>{item.i}</div>
                                </div>
                                <MoreVertIcon className="icon_widget_menu" onClick={(e) => handleMenuClick(e, item.i)} />
                            </div>
                            {/* <div className="box_drag_item_content_outer">
                                <div className="box_drag_item_content_inner">
                                    <RenderWidget componentName={item.i} />
                                </div>
                            </div> */}
                        </div>
                    ))}
                </ResponsiveReactGridLayout>
            </div>

            <Menu
                id="drag_item_menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                className="menu_drag_item"
                disablePortal
            >
                <MenuItem className="menu_item_delete" onClick={handleDelete}>{t('dashboard.widget.delete') || '위젯 삭제'}</MenuItem>
            </Menu>
        </div>
    );
};

export default CommonWidget;